import React, { useState, useEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { saveAs } from 'file-saver';
import "react-datepicker/dist/react-datepicker.css";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getAdjustedStartDate, getAdjustedEndDate } from '../../utils/dateHelper';
import LedgerDetailsOffcanvas from './LedgerDetailsOffcanvas';

const ProfitAndLossStatement = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateRangeFilter, setDateRangeFilter] = useState('yearly');
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedEndYear, setSelectedEndYear] = useState(null);
    const [showDrilldown, setShowDrilldown] = useState(false);
    const [selectedLedger, setSelectedLedger] = useState(null);
    const [hasFetched, setHasFetched] = useState(false);

    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const APIurl = process.env.REACT_APP_BACKEND_URL;

    const handleDateRangeChange = (value) => {
        setDateRangeFilter(value);
        if (value === 'yearly') {
            setSelectedYear(null);
            setSelectedEndYear(null);
            setStartDate('');
            setEndDate('');
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${APIurl}/api/projects`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,
            });
            setProjects(response.data?.Data);
        } catch (error) {
            console.error('Error fetching projects:', error);
        }
    };

    const fetchProfitLossData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (startDate) params.startdate = startDate;
            if (endDate) params.enddate = endDate;
            if (selectedProject) params.project_id = selectedProject;

            const userInfo = JSON.parse(localStorage.getItem("user-info"));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${APIurl}/api/profit-and-loss`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                    'Accept': 'application/json'
                },
                params,
                skipLoader: true
            });

            const processedData = processApiData(response.data.data);
            setData(processedData);
            setHasFetched(true);

        } catch (error) {
            console.error('Error fetching profit/loss data:', error);
        } finally {
            setLoading(false);
        }
    };

    const processApiData = (apiData) => {
        if (!apiData) return null;

        const processItems = (items) => {
            return items?.map(item => ({
                ...item,
                amount: parseFloat(item.amount) || 0,
                is_group: item.children && item.children.length > 0,
                children: item.children ? processItems(item.children) : []
            }));
        };
        return {
            ...apiData,
            gross_income_total: parseFloat(apiData.gross_income_total) || 0,
            gross_expense_total: parseFloat(apiData.gross_expense_total) || 0,
            gross_pl: parseFloat(apiData.gross_pl) || 0,
            net_income_total: parseFloat(apiData.net_income_total) || 0,
            net_expense_total: parseFloat(apiData.net_expense_total) || 0,
            net_pl: parseFloat(apiData.net_pl) || 0,
            gross_incomes: {
                ...apiData.gross_incomes,
                children: processItems(apiData.gross_incomes?.children || [])
            },
            gross_expenses: {
                ...apiData.gross_expenses,
                children: processItems(apiData.gross_expenses?.children || [])
            },
            net_incomes: {
                ...apiData.net_incomes,
                children: processItems(apiData.net_incomes?.children || [])
            },
            net_expenses: {
                ...apiData.net_expenses,
                children: processItems(apiData.net_expenses?.children || [])
            }
        };
    };

    const handleExport = async (format) => {
        try {
            if (format === 'pdf') {
                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const params = { startdate: startDate, enddate: endDate, format: 'pdf' };
                if (selectedProject) params.project_id = selectedProject;

                const response = await axios.get(`${APIurl}/api/reports/profit-loss`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin, 'Accept': 'application/pdf' },
                    params, responseType: 'blob'
                });
                saveAs(new Blob([response.data], { type: 'application/pdf' }), `ProfitAndLoss_${startDate || 'All'}_to_${endDate || 'All'}.pdf`);
            } else {
                const params = new URLSearchParams();
                if (startDate) params.append('startdate', startDate);
                if (endDate) params.append('enddate', endDate);
                if (selectedProject !== '') params.append('project_id', selectedProject);
                params.append('format', format);

                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const response = await axios.get(`${APIurl}/api/profit-and-loss?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin, 'Accept': 'application/octet-stream' },
                    responseType: 'blob', skipLoader: true
                });

                let filename = `profitloss.${format}`;
                const contentDisposition = response.headers['content-disposition'];
                if (contentDisposition && contentDisposition.includes('filename=')) {
                    filename = contentDisposition.split('filename=')[1].replace(/"/g, '').trim();
                }
                saveAs(new Blob([response.data], { type: format === 'csv' ? 'text/csv' : 'application/vnd.ms-excel' }), filename);
            }
        } catch (error) {
            console.error(`Error exporting report in ${format} format:`, error);
        }
    };

    const handleClearFilters = () => {
        setStartDate(''); setEndDate(''); setSelectedYear(null); setSelectedEndYear(null);
        setDateRangeFilter('yearly'); setSelectedProject('');
        setHasFetched(false);
        setData(null);
    };

    const formatCurrency = (amount) => {
        const absAmount = Math.abs(amount || 0);
        return new Intl.NumberFormat('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(absAmount);
    };

    const renderAccountItem = (item, index, type, depth) => {
        const amount = parseFloat(item.amount) || 0;
        
        let formattedAmount = '';
        if (amount === 0) {
            formattedAmount = '0.00';
        } else {
            const absValue = formatCurrency(amount);
            const isContra = (type === 'income' && item.cl_total_dc === 'D') || (type === 'expense' && item.cl_total_dc === 'C');
            formattedAmount = isContra ? `(${absValue})` : absValue;
        }

        return (
            <tr className="ledger-row text-secondary cursor-pointer" onClick={() => { setSelectedLedger({ ...item, type, is_group: false }); setShowDrilldown(true); }}>
                <td className="text-start" style={{ paddingLeft: `${depth * 20 + 15}px` }}>
                    {item.name}
                </td>
                <td className="text-end" style={{ width: '150px' }}>{formattedAmount}</td>
                <td className="text-end" style={{ width: '150px' }}></td>
            </tr>
        );
    };

    const renderAccountGroup = (items, type, depth = 0) => {
        if (!Array.isArray(items)) return null;
        return items.map((item, index) => {
            const amount = parseFloat(item.amount) || 0;
            if (amount === 0) return null;
            const isGroup = item.children && item.children.length > 0;
            
            if (isGroup) {
                return (
                    <React.Fragment key={`${depth}-${index}`}>
                        <tr className="ledger-row fw-bold text-dark">
                            <td className="text-start" style={{ paddingLeft: `${depth * 20 + 15}px` }}>{item.name}</td>
                            <td></td>
                            <td></td>
                        </tr>
                        {renderAccountGroup(item.children, type, depth + 1)}
                        <tr className="ledger-row fw-bold text-dark bg-light">
                            <td className="text-start" style={{ paddingLeft: `${depth * 20 + 15}px` }}>Total {item.name}</td>
                            <td className="text-end" style={{ width: '150px' }}></td>
                            <td className="text-end" style={{ width: '150px', borderTop: '1px solid #dee2e6' }}>{formatCurrency(amount)}</td>
                        </tr>
                    </React.Fragment>
                );
            } else {
                return renderAccountItem(item, index, type, depth);
            }
        });
    };

    return (
        <div className="main-content">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="page-title-box d-flex align-items-center justify-content-between">
                                <div><h4 className="mb-0 text-primary">Profit and Loss Statement</h4><p className="text-muted mb-0 small">Summary of revenues, costs, and expenses</p></div>
                                <div className="d-flex gap-2">
                                    <button onClick={() => handleExport('pdf')} className="btn btn-outline-danger btn-sm" disabled={loading || !data}><i className="fas fa-file-pdf me-1"></i> PDF</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="card shadow-sm border-0">
                                <div className="card-body p-3">
                                    <div className="row g-3 align-items-end">
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small mb-1">Date Range</label>
                                            <select className="form-select form-select-sm" value={dateRangeFilter} onChange={(e) => handleDateRangeChange(e.target.value)}>
                                                <option value="yearly">Yearly</option>
                                                <option value="custom">Custom Range</option>
                                            </select>
                                        </div>
                                        {dateRangeFilter === 'yearly' && (
                                            <>
                                                <div className="col-lg-2 col-md-3">
                                                    <label className="form-label font-size-12 text-uppercase fw-bold text-muted mb-1">Start Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker views={["year", "month", "day"]} openTo="year" format="DD-MM-YYYY" value={startDate ? dayjs(startDate) : null} onChange={(newValue) => { if (newValue) { setSelectedYear(newValue); if (!startDate || dayjs(startDate).year() !== newValue.year()) { setStartDate(newValue.startOf('year').format('YYYY-MM-DD')); setEndDate(newValue.endOf('year').format('YYYY-MM-DD')); setSelectedEndYear(newValue); } else { const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate); setStartDate(newStart); if (suggestedEndDate) setEndDate(suggestedEndDate); } } }} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                                                    </LocalizationProvider>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label text-muted small mb-1">End Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker views={["year", "month", "day"]} openTo="year" format="DD-MM-YYYY" value={endDate ? dayjs(endDate) : null} onChange={(newValue) => { if (newValue) { setSelectedEndYear(newValue); if (!endDate || dayjs(endDate).year() !== newValue.year()) { setEndDate(newValue.endOf('year').format('YYYY-MM-DD')); } else { setEndDate(getAdjustedEndDate(newValue, startDate)); } } }} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                                                    </LocalizationProvider>
                                                </div>
                                            </>
                                        )}
                                        {dateRangeFilter === 'custom' && (
                                            <>
                                                <div className="col-md-2">
                                                    <label className="form-label text-muted small mb-1">From Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker views={["year", "month", "day"]} format="DD-MM-YYYY" value={startDate ? dayjs(startDate) : null} onChange={(newValue) => { const { startDate: newStart, suggestedEndDate } = getAdjustedStartDate(newValue, endDate); setStartDate(newStart); if (suggestedEndDate) setEndDate(suggestedEndDate); }} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                                                    </LocalizationProvider>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label text-muted small mb-1">To Date</label>
                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                        <DatePicker views={["year", "month", "day"]} format="DD-MM-YYYY" value={endDate ? dayjs(endDate) : null} onChange={(newValue) => { setEndDate(getAdjustedEndDate(newValue, startDate)); }} slotProps={{ textField: { size: "small", fullWidth: true } }} />
                                                    </LocalizationProvider>
                                                </div>
                                            </>
                                        )}
                                        <div className="col-md-3">
                                            <label className="form-label text-muted small mb-1">Project</label>
                                            <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className="form-select form-select-sm"><option value="">All Projects</option>{projects.map(project => (<option key={project.id} value={project.id}>{project.name}</option>))}</select>
                                        </div>
                                        <div className="col-md-3 d-flex gap-2">
                                            <button className="btn btn-primary btn-sm px-4" onClick={fetchProfitLossData} disabled={loading}>{loading ? 'Applying...' : 'Apply Filters'}</button>
                                            <button className="btn btn-outline-secondary btn-sm px-4" onClick={handleClearFilters} disabled={loading}>Clear</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <div className="profit-loss-container">
                                {loading && !data ? (
                                    <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><p className="mt-3 text-muted">Analyzing financial performance...</p></div>
                                ) : !hasFetched ? (
                                    <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '16px' }}>
                                        Please apply a date filter to view the Profit and Loss report.
                                    </div>
                                ) : data ? (
                                        <div className="card shadow-sm border-0 w-100">
                                            <div className="card-body p-4 p-md-5">
                                                <div className="text-center mb-5">
                                                    <h3 className="text-uppercase fw-bold text-dark mb-1" style={{ letterSpacing: '1.5px' }}>Profit and Loss</h3>
                                                    <p className="text-muted mb-0">{startDate && endDate ? `For the period ${dayjs(startDate).format('DD MMM YYYY')} to ${dayjs(endDate).format('DD MMM YYYY')}` : 'For the selected period'}</p>
                                                </div>

                                                <div className="table-responsive">
                                                    <table className="table table-hover table-borderless" style={{ fontSize: '1rem' }}>
                                                        <thead className="border-bottom border-2 border-dark">
                                                            <tr>
                                                                <th className="text-start text-uppercase text-muted fw-bold pb-2">Account</th>
                                                                <th className="text-end text-uppercase text-muted fw-bold pb-2" style={{ width: '150px' }}></th>
                                                                <th className="text-end text-uppercase text-muted fw-bold pb-2" style={{ width: '150px' }}>Total (AED)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {/* REVENUE */}
                                                            <tr className="border-bottom border-secondary">
                                                                <td className="text-start fw-bold text-uppercase pt-4 pb-2 text-dark" style={{ letterSpacing: '0.5px' }}>Revenue</td>
                                                                <td></td>
                                                                <td></td>
                                                            </tr>
                                                            {renderAccountGroup(data.gross_incomes?.children || [], 'income')}
                                                            <tr className="border-top border-dark bg-light">
                                                                <td className="text-start fw-bold pb-3 pt-3 text-dark">Total Revenue</td>
                                                                <td></td>
                                                                <td className="text-end fw-bold pb-3 pt-3 text-dark">{formatCurrency(data.gross_income_total)}</td>
                                                            </tr>

                                                            {/* COST OF SALES */}
                                                            <tr className="border-bottom border-secondary">
                                                                <td className="text-start fw-bold text-uppercase pt-4 pb-2 text-dark" style={{ letterSpacing: '0.5px' }}>Cost of Sales</td>
                                                                <td></td>
                                                                <td></td>
                                                            </tr>
                                                            {renderAccountGroup(data.gross_expenses?.children || [], 'expense')}
                                                            <tr className="border-top border-dark bg-light">
                                                                <td className="text-start fw-bold pb-3 pt-3 text-dark">Total Cost of Sales</td>
                                                                <td></td>
                                                                <td className="text-end fw-bold pb-3 pt-3 text-dark">{formatCurrency(data.gross_expense_total)}</td>
                                                            </tr>

                                                            {/* GROSS PROFIT */}
                                                            <tr className="border-top border-bottom border-dark bg-light">
                                                                <td className="text-start fw-bold text-uppercase py-3 text-dark fs-6">Gross Profit</td>
                                                                <td></td>
                                                                <td className="text-end fw-bold py-3 text-dark fs-6">{formatCurrency(data.gross_pl)}</td>
                                                            </tr>

                                                            {/* OTHER INCOME */}
                                                            <tr className="border-bottom border-secondary">
                                                                <td className="text-start fw-bold text-uppercase pt-4 pb-2 text-dark" style={{ letterSpacing: '0.5px' }}>Other Income</td>
                                                                <td></td>
                                                                <td></td>
                                                            </tr>
                                                            {renderAccountGroup(data.net_incomes?.children || [], 'income')}
                                                            <tr className="border-top border-dark bg-light">
                                                                <td className="text-start fw-bold pb-3 pt-3 text-dark">Total Other Income</td>
                                                                <td></td>
                                                                <td className="text-end fw-bold pb-3 pt-3 text-dark">{formatCurrency(data.net_income_total)}</td>
                                                            </tr>

                                                            {/* OPERATING EXPENSES */}
                                                            <tr className="border-bottom border-secondary">
                                                                <td className="text-start fw-bold text-uppercase pt-4 pb-2 text-dark" style={{ letterSpacing: '0.5px' }}>Operating Expenses</td>
                                                                <td></td>
                                                                <td></td>
                                                            </tr>
                                                            {renderAccountGroup(data.net_expenses?.children || [], 'expense')}
                                                            <tr className="border-top border-dark bg-light">
                                                                <td className="text-start fw-bold pb-3 pt-3 text-dark">Total Operating Expenses</td>
                                                                <td></td>
                                                                <td className="text-end fw-bold pb-3 pt-3 text-dark">{formatCurrency(data.net_expense_total)}</td>
                                                            </tr>

                                                            {/* NET PROFIT */}
                                                            <tr className="border-top border-dark" style={{ borderBottom: '3px double #000' }}>
                                                                <td className="text-start fw-bold text-uppercase py-3 text-dark fs-5">Net Profit / (Loss)</td>
                                                                <td></td>
                                                                <td className="text-end fw-bold py-3 fs-5">
                                                                    <span className={data.net_pl < 0 ? 'text-danger' : 'text-success'}>
                                                                        {formatCurrency(data.net_pl)}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                ) : (
                                    <div className="text-center py-5"><p className="text-muted">No report data available for the selected period</p></div>
                                )}
                                <div className="report-footer text-muted small text-center mt-4">Report generated on {moment().format('MMM D, YYYY h:mm A')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .profit-loss-container { background: transparent; }
                .ledger-row:hover { background-color: #f8f9fa; }
                .table-hover tbody tr.ledger-row:hover td { background-color: #f8f9fa; }
            `}</style>

            {showDrilldown && selectedLedger && (
                <LedgerDetailsOffcanvas show={showDrilldown} onHide={() => { setShowDrilldown(false); setSelectedLedger(null); }} ledger={selectedLedger} startDate={startDate} endDate={endDate} projectId={selectedProject} />
            )}
        </div>
    );
};

export default ProfitAndLossStatement;
